// utils/transform-from-payload.ts
import { ValuationFormValues } from "@/store/valuationFormStore";

export function transformFromPayload(apiData: any): ValuationFormValues {
  // ── Nested objects (new API shape) ──────────────────────────────────────────
  const location         = apiData.location         || {};
  const coordinates      = location.coordinates     || {};
  const landParcel       = apiData.landParcel        || {};
  const mainBuilding     = apiData.mainBuilding      || {};
  const buildingComponents = apiData.buildingComponents || {};
  const valuationDetails = apiData.valuationDetails  || {};
  const amenitiesData    = apiData.amenitiesData     || {};
  const valuationTask    = apiData.valuationTaskDetails || {};
  const valuationStep    = apiData.valuationStep     || {};

  // Helper: pick first truthy value from a list of candidates
  const pick = (...values: any[]) => values.find(v => v !== undefined && v !== null && v !== "") ?? values[values.length - 1];

  return {
    // ── Identity ───────────────────────────────────────────────────────────────
    id:            pick(apiData.id, ""),
    upi:           pick(apiData.upi, ""),
    plotNumber:    pick(apiData.plot_number,   apiData.plotNumber,   ""),
    ownerIdNumber: pick(apiData.owner_id_number, apiData.ownerIdNumber, ""),
    ownerName:     pick(apiData.owner_name,    apiData.ownerName,    ""),

    // ── Location ───────────────────────────────────────────────────────────────
    country:       pick(location.country,      apiData.country,      ""),
    region:        pick(location.region,       apiData.region,       ""),
    subRegion:     pick(location.subRegion,    apiData.subRegion,    ""),
    county:        pick(location.county,       apiData.county,       ""),

    province:      pick(location.province,     apiData.province,     ""),
    district:      pick(location.district,     apiData.district,     ""),
    sector:        pick(location.sector,       apiData.sector,       ""),
    cell:          pick(location.cell,         apiData.cell,         ""),
    village:       pick(location.village,      apiData.village,      ""),
    subcounty:     pick(location.subcounty,    apiData.subcounty,    ""),
    parish:        pick(location.parish,       apiData.parish,       ""),
    constituency:  pick(location.constituency, apiData.constituency, ""),
    streetAddress: pick(location.streetAddress, apiData.streetAddress, ""),
    latitude:      pick(coordinates.latitude,  apiData.latitude,     0),
    longitude:     pick(coordinates.longitude, apiData.longitude,    0),

    // ── Valuation task meta ────────────────────────────────────────────────────
    institutionalId:        pick(valuationTask.institutionalId,        apiData.institutionalId,        ""),
    valuationAssignmentDate:pick(valuationTask.valuationAssignmentDate, apiData.valuationAssignmentDate,""),
    professionalCert:       pick(valuationTask.professionalCert,        apiData.professionalCert,       ""),
    principalValuer:        pick(valuationTask.principalValuer,         apiData.principalValuer,        ""),
    assistantValuer:        pick(valuationTask.assistantValuer,         apiData.assistantValuer,        ""),
    regulatoryBody:         pick(valuationTask.regulatoryBody,          apiData.regulatoryBody,         ""),

    // ── Step / workflow state ─────────────────────────────────────────────────
    step:              pick(valuationStep.step,              apiData.step,              0),
    estimatedDuration: pick(valuationStep.estimatedDuration, apiData.estimatedDuration, 0),
    timeRemaining:     pick(valuationStep.timeRemaining,     apiData.timeRemaining,     null),
    valuationStatus:   pick(valuationDetails.status,         apiData.valuationStatus,   "New"),

    // ── Land parcel ────────────────────────────────────────────────────────────
    landArea:      pick(landParcel.area,          apiData.land_area,       apiData.landArea,     0),
    landUse:       pick(landParcel.landUse,        apiData.land_use,        apiData.landUse,      ""),
    slopeCategory: pick(landParcel.slopeCategory,  apiData.slope_category,  apiData.slopeCategory,""),
    accessToRoad:  pick(landParcel.accessToRoad,   amenitiesData.accessToRoad, apiData.accessToRoad, false),
    zoning:        pick(apiData.zoning, ""),
    planningPermit:pick(apiData.planningPermit, null),
    tenure:        pick(apiData.tenure, null),

    // ── Valuation rates & values ───────────────────────────────────────────────
    landRate:  pick(valuationDetails.landRate,  apiData.land_rate,  apiData.landRate,  0),
    landValue: pick(valuationDetails.landValue, apiData.land_value, apiData.landValue, 0),

    mainHouseArea:  pick(valuationDetails.mainHouseArea,  apiData.main_house_area,  apiData.mainHouseArea,  0),
    mainHouseRate:  pick(valuationDetails.mainHouseRate,  apiData.main_house_rate,  apiData.mainHouseRate,  0),
    mainHouseValue: pick(apiData.main_house_value, apiData.mainHouseValue, 0),

    annexHouseArea:  pick(valuationDetails.annexHouseArea,  apiData.annex_house_area,  apiData.annexHouseArea,  0),
    annexRate:       pick(valuationDetails.annexRate,       apiData.annex_rate,        apiData.annexRate,       0),
    annexHouseValue: pick(apiData.annex_house_value, apiData.annexHouseValue, 0),

    fenceLength: pick(valuationDetails.fenceLength, apiData.fence_length, apiData.fenceLength, 0),
    fenceRate:   pick(valuationDetails.fenceRate,   apiData.fence_rate,   apiData.fenceRate,   0),
    fenceValue:  pick(apiData.fence_value, apiData.fenceValue, 0),
    hasFence:    pick(apiData.hasFence, false),

    retainingWallsLength:    pick(valuationDetails.retainingWallsLength,    apiData.retaining_walls_length,    apiData.retainingWallsLength,    0),
    retainingWallRate:       pick(valuationDetails.retainingWallRate,       apiData.retaining_wall_rate,       apiData.retainingWallRate,       0),
    retainingWallsValue:     pick(apiData.retaining_walls_value, apiData.retainingWallsValue, 0),
    hasRetainingWalls:       pick(apiData.hasRetainingWalls, false),

    entranceGateLength: pick(valuationDetails.entranceGateLength, apiData.entrance_gate_length, apiData.entranceGateLength, 0),
    entranceGateRate:   pick(valuationDetails.entranceGateRate,   apiData.entrance_gate_rate,   apiData.entranceGateRate,   0),
    entranceGateValue:  pick(apiData.entrance_gate_value, apiData.entranceGateValue, 0),

    pavedArea:      pick(valuationDetails.pavedArea,     apiData.paved_area,       apiData.pavedArea,      0),
    pavedAreaRate:  pick(valuationDetails.pavedAreaRate, apiData.paved_area_rate,  apiData.pavedAreaRate,  0),
    pavedAreaValue: pick(apiData.paved_area_value, apiData.pavedAreaValue, 0),

    septicTankNumber: pick(valuationDetails.septicTankNumber, apiData.septic_tank_number, apiData.septicTankNumber, 0),
    septicTankValue:  pick(valuationDetails.septicTankValue,  apiData.septic_tank_value,  apiData.septicTankValue,  0),
    septicTanksValue: pick(apiData.septic_tanks_value, apiData.septicTanksValue, 0),

    gardenArea:  pick(valuationDetails.gardenArea,  apiData.garden_area,  apiData.gardenArea,  0),
    gardenRate:  pick(valuationDetails.gardenRate,  apiData.garden_rate,  apiData.gardenRate,  0),
    gardenValue: pick(apiData.garden_value, apiData.gardenValue, 0),

    verandahArea:  pick(valuationDetails.verandahArea,  apiData.verandah_area,  apiData.verandahArea,  0),
    verandahRate:  pick(valuationDetails.verandahRate,  apiData.verandah_rate,  apiData.verandahRate,  0),
    verandahValue: pick(apiData.verandah_value, apiData.verandahValue, 0),
    hasVeranda:    pick(apiData.hasVeranda, false),

    landscapingValue: pick(valuationDetails.landscapingValue, apiData.landscaping_value, apiData.landscapingValue, 0),

    gateFenceLength: pick(valuationDetails.gateFenceLength, apiData.gate_fence_length, apiData.gateFenceLength, 0),

    forcedSaleDiscount: pick(valuationDetails.forcedSaleDiscount, apiData.forced_sale_discount, apiData.forcedSaleDiscount, 0),
    depreciationRate:   pick(valuationDetails.depreciationRate,   apiData.depreciation_rate,    apiData.depreciationRate,   0),

    // ── Calculated / summary values ────────────────────────────────────────────
    buildingValue:    pick(valuationDetails.buildingValue,    apiData.buildingValue,    0),
    amenitiesValue:   pick(valuationDetails.amenitiesValue,   apiData.amenitiesValue,   0),
    depreciationValue:pick(valuationDetails.depreciationValue,apiData.depreciationValue,0),
    improvementsValue:pick(valuationDetails.improvementsValue,apiData.improvementsValue,0),
    openMarketValue:  pick(valuationDetails.openMarketValue,  apiData.openMarketValue,  0),
    forcedSaleValue:  pick(valuationDetails.forcedSaleValue,  apiData.forcedSaleValue,  0),
    insuranceValue:   pick(valuationDetails.insuranceValue,   apiData.insuranceValue,   0),

    totalPropertyValue:                    pick(valuationDetails.totalPropertyValue,                    apiData.total_property_value,                    apiData.totalPropertyValue,                    0),
    totalPropertyValueAfterForcedSale:     pick(apiData.total_property_value_after_forced_sale,         apiData.totalPropertyValueAfterForcedSale,         0),
    totalPropertyValueAfterDepreciation:   pick(apiData.total_property_value_after_depreciation,        apiData.totalPropertyValueAfterDepreciation,       0),

    valuationDate: pick(valuationDetails.valuationDate, apiData.valuationDate, ""),
    collectorNotes:pick(valuationDetails.collectorNotes, apiData.collectorNotes, ""),
    approvedBy:    pick(valuationDetails.approvedBy,     apiData.approvedBy,    ""),
    approvedDate:  pick(valuationDetails.approvedDate,   apiData.approvedDate,  ""),
    assignedCollector:    pick(valuationDetails.assignedCollector,     apiData.assignedCollector,    ""),
    assignedInstitutional:pick(apiData.assignedInstitutional, ""),

    // ── Main building ──────────────────────────────────────────────────────────
    constructedArea:          pick(mainBuilding.constructedArea,       apiData.constructed_area,          apiData.constructedArea,          0),
    yearOfConstruction:       pick(mainBuilding.constructionYear,      apiData.year_of_construction,      apiData.yearOfConstruction,       new Date().getFullYear()),
    mainHouseFloors:          pick(mainBuilding.numberOfFloors,        apiData.main_house_floors,         apiData.mainHouseFloors,          1),
    numberOfFloors:           pick(mainBuilding.numberOfFloors,        apiData.numberOfFloors,            1),
    constructionQuality:      pick(mainBuilding.constructionQuality,   apiData.constructionQuality,       ""),
    exteriorFinish:           pick(mainBuilding.exteriorFinish,        apiData.exteriorFinish,            ""),
    roofType:                 pick(mainBuilding.roofType,              apiData.roof_type,                 apiData.roofType,                 "Iron Sheets"),
    maintenanceCondition:     pick(mainBuilding.maintenanceCondition,  apiData.maintenanceCondition,      0),
    bedrooms:                 pick(mainBuilding.bedrooms,              apiData.bedrooms,                  0),
    bathrooms:                pick(mainBuilding.bathrooms,             apiData.bathrooms,                 0),
    floorCondition:           pick(mainBuilding.floorCondition,        apiData.floor_condition,           apiData.floorCondition,           "Excellent"),
    wallsCondition:           pick(mainBuilding.wallsCondition,        apiData.walls_condition,           apiData.wallsCondition,           "Excellent"),
    windowsCondition:         pick(mainBuilding.windowsCondition,      apiData.windows_condition,         apiData.windowsCondition,         "Excellent"),
    doorsCondition:           pick(mainBuilding.doorsCondition,        apiData.doors_condition,           apiData.doorsCondition,           "Excellent"),
    roofCondition:            pick(mainBuilding.roofCondition,         apiData.roof_condition,            apiData.roofCondition,            "Excellent"),

    // ── Building component descriptions ───────────────────────────────────────
    outsideKitchenDescription: pick(buildingComponents.outsideKitchenDescription, apiData.outside_kitchen_description, apiData.outsideKitchenDescription, ""),
    waterTankDescription:      pick(buildingComponents.waterTankDescription,      apiData.water_tank_description,      apiData.waterTankDescription,      ""),
    landscapingDescription:    pick(buildingComponents.landscapingDescription,    apiData.landscaping_description,     apiData.landscapingDescription,    ""),
    soilDrainageDescription:   pick(buildingComponents.soilDrainageDescription,   apiData.soil_drainage_description,   apiData.soilDrainageDescription,   ""),
    pvcPipesDescription:       pick(buildingComponents.pvcPipesDescription,       apiData.pvc_pipes_description,       apiData.pvcPipesDescription,       ""),
    washTapsDescription:       pick(buildingComponents.washTapsDescription,       apiData.wash_taps_description,       apiData.washTapsDescription,       ""),
    fixturesFittingsDescription:pick(buildingComponents.fixturesFittingsDescription, apiData.fixtures_fittings_description, apiData.fixturesFittingsDescription, ""),

    // ── Amenities / boolean flags ─────────────────────────────────────────────
    hasAnnexBuilding:   pick(amenitiesData.hasAnnexBuilding,   apiData.has_annex_building,    apiData.hasAnnexBuilding,   false),
    hasWater:           pick(amenitiesData.hasWater,           apiData.has_water_tank,         apiData.hasWater,           apiData.hasWaterTank,       false),
    hasElectricity:     pick(amenitiesData.hasElectricity,     apiData.has_electricity,        apiData.hasElectricity,     false),
    hasOutsideKitchen:  pick(amenitiesData.hasOutsideKitchen,  apiData.has_outside_kitchen,    apiData.hasOutsideKitchen,  false),
    hasGarden:          pick(amenitiesData.hasGarden,          apiData.has_garden,             apiData.hasGarden,          false),
    hasPavedParking:    pick(amenitiesData.hasPavedParking,    apiData.has_paved_parking,      apiData.hasPavedParking,    false),
    hasSewage:          pick(apiData.hasSewage,    false),
    hasInternet:        pick(apiData.hasInternet,  false),
    hasParkingSpace:    pick(apiData.hasParkingSpace, false),
    hasWaterTank:       pick(apiData.hasWaterTank, amenitiesData.hasWater, false),

    // ── Depreciation / cost approach ─────────────────────────────────────────
    effectiveAge:             pick(apiData.effective_age,              apiData.effectiveAge,             0),
    annualDepreciationRate:   pick(valuationDetails.annualDepreciationRate, apiData.annual_depreciation_rate, apiData.annualDepreciationRate, 0),
    unitPrice:                pick(apiData.unit_price,                 apiData.unitPrice,                0),
    originalCostOfConstruction:pick(valuationDetails.originalCostOfConstruction, apiData.original_cost_of_construction, apiData.originalCostOfConstruction, 0),
    maintenanceCoefficient:   pick(valuationDetails.maintenanceCoefficient, apiData.maintenance_coefficient, apiData.maintenanceCoefficient, 0),
    depreciatedValue:         pick(valuationDetails.depreciatedValue,  apiData.depreciated_value,        apiData.depreciatedValue,         0),
    presentValue:             pick(valuationDetails.presentValue,      apiData.present_value,            apiData.presentValue,             0),

    // ── Condition fields ──────────────────────────────────────────────────────
    mainHouseCondition:      pick(apiData.main_house_condition,      apiData.mainHouseCondition,      ""),
    annexHouseCondition:     pick(apiData.annex_house_condition,     apiData.annexHouseCondition,     ""),
    wallCondition:           pick(apiData.wall_condition,            apiData.wallCondition,           ""),
    fenceCondition:          pick(apiData.fence_condition,           apiData.fenceCondition,          ""),
    retainingWallsCondition: pick(apiData.retaining_walls_condition, apiData.retainingWallsCondition, ""),
    entranceGateCondition:   pick(apiData.entrance_gate_condition,   apiData.entranceGateCondition,   ""),
    pavedAreaCondition:      pick(apiData.paved_area_condition,      apiData.pavedAreaCondition,      ""),
    septicTanksCondition:    pick(apiData.septic_tanks_condition,    apiData.septicTanksCondition,    ""),
    gardenCondition:         pick(apiData.garden_condition,          apiData.gardenCondition,         ""),
    verandahCondition:       pick(apiData.verandah_condition,        apiData.verandahCondition,       ""),
    annexHouseFloors:        pick(apiData.annex_house_floors,        apiData.annexHouseFloors,        1),

    // ── Step 1 descriptive ────────────────────────────────────────────────────
    dateOfVisit:      pick(apiData.date_of_visit,   apiData.dateOfVisit,   ""),
    propertyName:     pick(apiData.property_name,   apiData.propertyName,  ""),
    valuationPurpose: pick(apiData.valuation_purpose, apiData.valuationPurpose, ""),
    propertyType:     pick(apiData.property_type,   apiData.propertyType,  ""),

    // ── Misc numeric ──────────────────────────────────────────────────────────
    area:      pick(apiData.area,      0),
    age:       pick(apiData.age,       0),
    condition: pick(apiData.condition, 0),

    // ── Photos (flat fields) ──────────────────────────────────────────────────
    frontView:      pick(apiData.front_view,      apiData.frontView,      null),
    backView:       pick(apiData.back_view,        apiData.backView,       null),
    leftView:       pick(apiData.left_view,        apiData.leftView,       null),
    rightView:      pick(apiData.right_view,       apiData.rightView,      null),
    livingRoom:     pick(apiData.living_room,      apiData.livingRoom,     null),
    kitchen:        pick(apiData.kitchen,          null),
    bedroom:        pick(apiData.bedroom,          null),
    bathroom:       pick(apiData.bathroom,         null),
    diningRoom:     pick(apiData.dining_room,      apiData.diningRoom,     null),
    veranda:        pick(apiData.veranda,          null),
    googleEarthOne: pick(apiData.google_earth_one, apiData.googleEarthOne, null),
    googleEarthTwo: pick(apiData.google_earth_two, apiData.googleEarthTwo, null),
    masterPlan:     pick(apiData.master_plan,      apiData.masterPlan,     apiData.masterPlanPhoto, null),

    // ── Legacy photo fields ───────────────────────────────────────────────────
    googleEarth:    pick(apiData.google_earth,     apiData.googleEarth,    null),
    cityMasterPlan: pick(apiData.city_master_plan, apiData.cityMasterPlan, null),
    photo:          pick(apiData.photo,            null),

    // ── Photos array (hydrated from propertyPhotos if present) ───────────────
    photos: (() => {
      if (Array.isArray(apiData.photos) && apiData.photos.length) return apiData.photos;
      if (Array.isArray(apiData.propertyPhotos) && apiData.propertyPhotos.length) return apiData.propertyPhotos;
      return [];
    })(),

    // ── Documents array ───────────────────────────────────────────────────────
    documents: apiData.documents || [],

    // ── Remarks / notes ───────────────────────────────────────────────────────
    remarks:         pick(apiData.remarks,          ""),
    recommendations: pick(apiData.recommendations,  ""),
    additionalNotes: pick(apiData.additional_notes, apiData.additionalNotes, ""),

    // ── Audit ─────────────────────────────────────────────────────────────────
    createdAt: pick(apiData.createdAt, ""),
    updatedAt: pick(apiData.updatedAt, ""),

    // ── Viewer / collector IDs ─────────────────────────────────────────────────
    viewerOwnerId:      pick(apiData.viewerOwnerId,      ""),
    collectorId:        pick(apiData.collectorId,        null),
    viewerCollectorId:  pick(apiData.viewerCollectorId,  null),
    supervisorId:       pick(apiData.supervisorId,       null),
    viewerSupervisorId: pick(apiData.viewerSupervisorId, null),
    phone:              pick(apiData.phone,              ""),
    status:             pick(apiData.status,             "draft"),
    certifiedAt:        pick(apiData.certifiedAt,        null),
    confidenceLevel:    pick(apiData.confidenceLevel,    null),
  };
}